import { getDefaultClient } from '@dynamic-labs-sdk/client';
import { getWalletProviderFromWalletAccount } from '@dynamic-labs-sdk/client/core';
import type {
  SendOptions,
  Transaction,
  VersionedTransaction,
} from '@solana/web3.js';

import type { SolanaWalletAccount } from '../SolanaWalletAccount.types';
import { NotSolanaProviderError } from '../errors/NotSolanaProviderError';
import { isSolanaWalletProvider } from '../isSolanaWalletProvider';

type SignAndSendTransactionParams = {
  options?: SendOptions;
  transaction: Transaction | VersionedTransaction;
  walletAccount: SolanaWalletAccount;
};

/**
 * Signs and sends a Solana transaction
 * @param params.transaction - The transaction to sign and send
 * @param params.walletAccount - The wallet account to sign and send the transaction with
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns An object containing the signature
 */
export const signAndSendTransaction = async (
  { walletAccount, transaction, options }: SignAndSendTransactionParams,
  client = getDefaultClient()
): Promise<{ signature: string }> => {
  const provider = getWalletProviderFromWalletAccount(
    {
      walletAccount,
    },
    client
  );

  if (!isSolanaWalletProvider(provider)) {
    throw new NotSolanaProviderError({ providerKey: provider.key });
  }

  return provider.signAndSendTransaction({
    options,
    transaction,
    walletAccount,
  });
};
